import React, { useState, useEffect, useCallback } from "react";
import api from '../../lib/axios';
import { toast } from "sonner";
import {
  Database,
  Download,
  Upload,
  Trash2,
  RefreshCw,
  HardDrive,
  Calendar,
  FileText,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Settings,
  Play,
  ToggleLeft,
  ToggleRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * BackupManager - Component for managing MongoDB backups
 */
const BackupManager = ({ isOpen, onClose }) => {
  const [backups, setBackups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [dropExisting, setDropExisting] = useState(false);
  
  // Auto backup config state
  const [autoConfig, setAutoConfig] = useState({
    enabled: false,
    schedule: "daily",
    time: "02:00",
    retention_days: 7,
    last_backup: null,
    last_backup_status: null
  });
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [isRunningAutoBackup, setIsRunningAutoBackup] = useState(false);

  const fetchBackups = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get(`${API}/backup/list`);
      setBackups(response.data.backups || []);
    } catch (error) {
      toast.error("Error al cargar backups");
      // console.error(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchAutoConfig = useCallback(async () => {
    try {
      const response = await api.get(`${API}/backup/auto/config`);
      setAutoConfig(response.data);
    } catch (error) {
      console.error("Error loading auto backup config:", error?.message || error);
      // Non-critical error - auto backup config is optional
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchBackups();
      fetchAutoConfig();
    }
  }, [isOpen, fetchBackups, fetchAutoConfig]);

  const handleCreateBackup = async () => {
    setIsCreating(true);
    try {
      const response = await api.post(`${API}/backup/create`);
      toast.success(`Backup creado: ${response.data.backup_name}`);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear backup");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    try {
      await api.delete(`${API}/backup/${selectedBackup.backup_name}`);
      toast.success("Backup eliminado");
      setShowDeleteConfirm(false);
      setSelectedBackup(null);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar backup");
    }
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    setIsRestoring(true);
    try {
      const response = await api.post(`${API}/backup/restore`, {
        backup_name: selectedBackup.backup_name,
        drop_existing: dropExisting
      });
      toast.success(`Restaurado: ${response.data.total_documents} documentos`);
      setShowRestoreConfirm(false);
      setSelectedBackup(null);
      setDropExisting(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar backup");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleSaveAutoConfig = async () => {
    setIsSavingConfig(true);
    try {
      await api.put(`${API}/backup/auto/config`, {
        enabled: autoConfig.enabled,
        schedule: autoConfig.schedule,
        time: autoConfig.time,
        retention_days: autoConfig.retention_days
      });
      toast.success("Configuración guardada");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar configuración");
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleRunAutoBackup = async () => {
    setIsRunningAutoBackup(true);
    try {
      const response = await api.post(`${API}/backup/auto/run`);
      toast.success(`Backup automático creado: ${response.data.backup?.backup_name}`);
      fetchBackups();
      fetchAutoConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al ejecutar backup automático");
    } finally {
      setIsRunningAutoBackup(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "—";
    const date = new Date(timestamp);
    return date.toLocaleString("es-MX", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              Administrador de Backups
            </DialogTitle>
            <DialogDescription>
              Crea, restaura y gestiona copias de seguridad de la base de datos
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Tabs defaultValue="backups" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="backups" data-testid="tab-backups">
                  <HardDrive className="w-4 h-4 mr-2" />
                  Backups
                </TabsTrigger>
                <TabsTrigger value="auto" data-testid="tab-auto-backup">
                  <Clock className="w-4 h-4 mr-2" />
                  Automático
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="backups" className="space-y-4">
                {/* Actions Bar */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleCreateBackup}
                      disabled={isCreating}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="create-backup-btn"
                    >
                      {isCreating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Creando...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Crear Backup
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={fetchBackups}
                      disabled={isLoading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                  </div>
                  <Badge variant="secondary" className="text-sm">
                    {backups.length} backup{backups.length === 1 ? '' : 's'} disponible{backups.length === 1 ? '' : 's'}
                  </Badge>
                </div>

                {/* Backups Table */}
                <Card>
                  <CardContent className="p-0">
                    {backups.length === 0 ? (
                      <div className="text-center py-12">
                        <HardDrive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-500">No hay backups disponibles</p>
                        <p className="text-slate-400 text-sm mt-1">Crea tu primer backup para proteger tus datos</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nombre</TableHead>
                            <TableHead>Fecha</TableHead>
                            <TableHead className="text-right">Colecciones</TableHead>
                            <TableHead className="text-right">Documentos</TableHead>
                            <TableHead className="text-right">Tamaño</TableHead>
                            <TableHead className="text-right">Acciones</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {backups.map((backup) => (
                            <TableRow key={backup.backup_name}>
                              <TableCell className="font-mono text-sm">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-slate-400" />
                                  {backup.backup_name}
                                  {backup.backup_name.startsWith("auto_") && (
                                    <Badge variant="outline" className="text-xs">Auto</Badge>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm text-slate-600">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(backup.timestamp)}
                                </div>
                              </TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline">{backup.collections_count}</Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono">
                                {backup.total_documents}
                              </TableCell>
                              <TableCell className="text-right text-sm text-slate-500">
                                {backup.size_mb} MB
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedBackup(backup);
                                      setShowRestoreConfirm(true);
                                    }}
                                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                    data-testid={`restore-backup-${backup.backup_name}`}
                                  >
                                    <Upload className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      setSelectedBackup(backup);
                                      setShowDeleteConfirm(true);
                                    }}
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    data-testid={`delete-backup-${backup.backup_name}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="auto" className="space-y-4">
                {/* Auto Backup Configuration */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Settings className="w-5 h-5" />
                      Configuración de Backup Automático
                    </CardTitle>
                    <CardDescription>
                      Programa backups automáticos para proteger tus datos
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {autoConfig.enabled ? (
                          <ToggleRight className="w-6 h-6 text-emerald-600" />
                        ) : (
                          <ToggleLeft className="w-6 h-6 text-slate-400" />
                        )}
                        <div>
                          <p className="font-medium">Backups Automáticos</p>
                          <p className="text-sm text-slate-500">
                            {autoConfig.enabled ? "Activado" : "Desactivado"}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={autoConfig.enabled}
                        onCheckedChange={(checked) => 
                          setAutoConfig({ ...autoConfig, enabled: checked })
                        }
                        data-testid="auto-backup-toggle"
                      />
                    </div>
                    
                    {/* Schedule Configuration */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="schedule">Frecuencia</Label>
                        <Select
                          value={autoConfig.schedule}
                          onValueChange={(value) => 
                            setAutoConfig({ ...autoConfig, schedule: value })
                          }
                          disabled={!autoConfig.enabled}
                        >
                          <SelectTrigger id="schedule" data-testid="backup-schedule">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Diario</SelectItem>
                            <SelectItem value="weekly">Semanal (Domingo)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="time">Hora</Label>
                        <Input
                          id="time"
                          type="time"
                          value={autoConfig.time}
                          onChange={(e) => 
                            setAutoConfig({ ...autoConfig, time: e.target.value })
                          }
                          disabled={!autoConfig.enabled}
                          data-testid="backup-time"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="retention">Retención (días)</Label>
                      <Input
                        id="retention"
                        type="number"
                        min="1"
                        max="30"
                        value={autoConfig.retention_days}
                        onChange={(e) => 
                          setAutoConfig({ ...autoConfig, retention_days: parseInt(e.target.value) || 7 })
                        }
                        disabled={!autoConfig.enabled}
                        className="w-32"
                        data-testid="backup-retention"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Se mantendrán los últimos {autoConfig.retention_days} backups automáticos
                      </p>
                    </div>
                    
                    {/* Last Backup Status */}
                    {autoConfig.last_backup && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-600">
                          <strong>Último backup:</strong> {formatDate(autoConfig.last_backup)}
                          {autoConfig.last_backup_status === "success" ? (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-700">Exitoso</Badge>
                          ) : autoConfig.last_backup_status === "failed" ? (
                            <Badge className="ml-2 bg-red-100 text-red-700">Fallido</Badge>
                          ) : null}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-4 border-t">
                      <Button
                        onClick={handleSaveAutoConfig}
                        disabled={isSavingConfig}
                        data-testid="save-auto-config"
                      >
                        {isSavingConfig ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Guardando...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Guardar Configuración
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleRunAutoBackup}
                        disabled={isRunningAutoBackup}
                        data-testid="run-auto-backup"
                      >
                        {isRunningAutoBackup ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Ejecutando...
                          </>
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-2" />
                            Ejecutar Ahora
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">Recomendaciones de Backup</p>
                    <ul className="text-blue-700 mt-1 space-y-1">
                      <li>• Crea backups regularmente antes de hacer cambios importantes</li>
                      <li>• Los backups se guardan localmente en el servidor</li>
                      <li>• Al restaurar, puedes elegir si eliminar datos existentes o fusionar</li>
                      <li>• Los backups automáticos se ejecutan según la configuración programada</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Backup
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar el backup <strong>{selectedBackup?.backup_name}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedBackup(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteBackup}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Confirmation */}
      <AlertDialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Restaurar Backup
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Vas a restaurar el backup <strong>{selectedBackup?.backup_name}</strong> con {selectedBackup?.total_documents} documentos.
                </p>
                <div className="flex items-center space-x-2 p-3 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="drop-existing"
                    checked={dropExisting}
                    onCheckedChange={setDropExisting}
                  />
                  <Label htmlFor="drop-existing" className="text-sm cursor-pointer">
                    <span className="font-medium text-red-600">Eliminar datos existentes</span>
                    <span className="text-slate-500 ml-1">(reemplazar completamente)</span>
                  </Label>
                </div>
                {!dropExisting && (
                  <p className="text-sm text-amber-600">
                    Sin esta opción, los datos del backup se agregarán a los existentes (puede causar duplicados).
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedBackup(null);
              setDropExisting(false);
            }}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreBackup}
              disabled={isRestoring}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isRestoring ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Restaurando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Restaurar
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BackupManager;
